import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolves a URL param that may be either a UUID or a profile_slug into
 * a seller user UUID. UUIDs pass through; slugs are resolved via RPC.
 */
export function useResolvedSellerId(param: string | undefined) {
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState<boolean>(!!param);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!param) {
        setResolvedId(null);
        setIsResolving(false);
        return;
      }

      if (UUID_RE.test(param)) {
        setResolvedId(param);
        setIsResolving(false);
        setError(null);
        return;
      }

      setIsResolving(true);
      try {
        const { data, error } = await (supabase as any).rpc("get_seller_id_by_slug", {
          slug: param,
        });
        if (cancelled) return;
        if (error || !data) {
          setError("Seller not found");
          setResolvedId(null);
        } else {
          setResolvedId(data as string);
          setError(null);
        }
      } catch (e) {
        if (cancelled) return;
        setError("Seller not found");
        setResolvedId(null);
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [param]);

  return { resolvedId, isResolving, error };
}
